import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Table, Space, Tooltip, Card } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import useApiCall from "../common/api-call";
import {
  listAnnounce,
  getAnnounce,
  upsertAnnounce,
  deleteAnnounce,
} from "./notification-service";
import { Spin } from "antd";
import dayjs from "dayjs";

const { TextArea } = Input;

const NotificationListView = () => {
  const { apiCall } = useApiCall();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    id: null,
    createdBy: "",
    createdAt: "",
  });
  const [originalFormData, setOriginalFormData] = useState({
    title: "",
    content: "",
    id: null,
    createdBy: "",
    createdAt: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [titleError, setTitleError] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [contentTouched, setContentTouched] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchNotifications = async () => {
    setLoading(true);
    const res = await listAnnounce(apiCall);
    setNotifications(res?.items || res || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleShowDetail = async item => {
    const detail = await getAnnounce(apiCall, item.id);
    setSelected(detail || item);
    setDetailOpen(true);
  };

  const handleOpenForm = async (item = null) => {
    if (item) {
      setActionLoading("edit");
      try {
        const detail = await getAnnounce(apiCall, item.id);
        setFormData({
          id: item.id,
          title: item.title,
          content: detail?.content || "",
          createdBy: item.createdBy,
          createdAt: item.createdAt,
        });
        setOriginalFormData({
          id: item.id,
          title: item.title,
          content: detail?.content || "",
          createdBy: item.createdBy,
          createdAt: item.createdAt,
        });
        setEditMode(true);
      } finally {
        setActionLoading(null);
      }
    } else {
      setFormData({
        id: null,
        title: "",
        content: "",
        createdBy: "",
        createdAt: "",
      });
      setOriginalFormData({
        id: null,
        title: "",
        content: "",
        createdBy: "",
        createdAt: "",
      });
      setEditMode(false);
    }
    setTitleError("");
    setTitleTouched(false);
    setContentTouched(false);
    setFormOpen(true);
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === "title") {
      setTitleTouched(true);
      if (!editMode && notifications.some(n => n.title === value)) {
        setTitleError("Title already exists");
      } else if (
        editMode &&
        notifications.some(n => n.title === value && n.id !== formData.id)
      ) {
        setTitleError("Title already exists");
      } else {
        setTitleError("");
      }
    }
    if (name === "content") {
      setContentTouched(true);
    }
  };

  const isFormChanged = () =>
    formData.title !== originalFormData.title ||
    formData.content !== originalFormData.content;

  const isAddValid = () => {
    if (!formData.title.trim() || !formData.content.trim()) return false;
    if (notifications.some(n => n.title === formData.title)) return false;
    return true;
  };

  const isEditValid = () => {
    if (!isFormChanged()) return false;
    if (!formData.title.trim() || !formData.content.trim()) return false;
    if (
      notifications.some(
        n => n.title === formData.title && n.id !== formData.id,
      )
    )
      return false;
    return true;
  };

  const handleFormSubmit = async () => {
    setActionLoading(formData.id ? "save" : "add");
    await upsertAnnounce(
      apiCall,
      formData.id,
      formData.title,
      formData.content,
    );
    setActionLoading(null);
    setFormOpen(false);
    fetchNotifications();
  };

  const handleCancelForm = () => {
    setFormOpen(false);
    setTitleError("");
    setTitleTouched(false);
    setContentTouched(false);
  };

  const handleAskDelete = item => {
    setDeleteTarget(item);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    setActionLoading("delete");
    await deleteAnnounce(apiCall, deleteTarget.id);
    setActionLoading(null);
    setConfirmDeleteOpen(false);
    setDeleteTarget(null);
    fetchNotifications();
    setDetailOpen(false);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setDeleteTarget(null);
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text || ""} placement="top">
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text || ""} placement="top">
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text || ""} placement="top">
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {text ? dayjs(text).format("YYYY-MM-DD HH:mm:ss") : ""}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit" placement="top">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenForm(record)}
              disabled={!!actionLoading}
              loading={
                (actionLoading === "save" || actionLoading === "edit") &&
                formData.id === record.id
              }
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleAskDelete(record)}
              disabled={!!actionLoading}
              loading={actionLoading === "delete" && selected?.id === record.id}
            >
              Delete
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ flexGrow: 1, minHeight: "100vh" }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenForm()}
          disabled={!!actionLoading}
          loading={actionLoading === "add"}
        >
          Add Notification
        </Button>
      </Space>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
          <Spin />
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ marginTop: 32, marginLeft: 16, color: "#666" }}>
          No notifications yet, please add one.
        </div>
      ) : (
        <Card>
          <Table
            dataSource={notifications}
            columns={columns}
            rowKey="id"
            size="middle"
            pagination={false}
          />
        </Card>
      )}

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        width="600px"
        title="Notification Detail"
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)} disabled={!!actionLoading}>
            Close
          </Button>,
          <Button
            key="delete"
            danger
            onClick={() => handleAskDelete(selected)}
            disabled={!!actionLoading}
            loading={actionLoading === "delete"}
          >
            Delete
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setDetailOpen(false);
              handleOpenForm(selected);
            }}
            disabled={!!actionLoading}
          >
            Edit
          </Button>,
        ]}
      >
        <h6 style={{ marginBottom: 16 }}>{selected?.title}</h6>
        <div>{selected?.content}</div>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        width="600px"
        title={editMode ? "Edit Notification" : "Add Notification"}
        footer={[
          <Button key="cancel" onClick={handleCancelForm} disabled={!!actionLoading}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleFormSubmit}
            disabled={
              !!actionLoading || (editMode ? !isEditValid() : !isAddValid())
            }
            loading={actionLoading === "save" || actionLoading === "add"}
          >
            {editMode ? "Save" : "Add"}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>Title</div>
          <Input
            name="title"
            value={formData.title}
            onChange={handleFormChange}
            status={!!titleError || (titleTouched && !formData.title.trim()) ? "error" : ""}
          />
          {(titleError || (titleTouched && !formData.title.trim())) && (
            <div style={{ color: "#ff4d4f", fontSize: "14px", marginTop: 4 }}>
              {titleError || "Title cannot be empty"}
            </div>
          )}
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>Content</div>
          <TextArea
            name="content"
            value={formData.content}
            onChange={handleFormChange}
            rows={4}
            status={contentTouched && !formData.content.trim() ? "error" : ""}
          />
          {contentTouched && !formData.content.trim() && (
            <div style={{ color: "#ff4d4f", fontSize: "14px", marginTop: 4 }}>
              Content cannot be empty
            </div>
          )}
        </div>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        open={confirmDeleteOpen}
        onCancel={handleCancelDelete}
        width="400px"
        title="Confirm Delete"
        footer={[
          <Button key="cancel" onClick={handleCancelDelete} disabled={!!actionLoading}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleConfirmDelete}
            disabled={!!actionLoading}
            loading={actionLoading === "delete"}
          >
            Confirm
          </Button>,
        ]}
      >
        <div>Are you sure you want to delete this notification?</div>
        <div style={{ marginTop: 8, color: "#666" }}>{deleteTarget?.title}</div>
      </Modal>
    </div>
  );
};

export default NotificationListView;
